import { useState, type ReactNode } from 'react'

import type { Route } from '../../app/nav'
import { cn } from '../../lib/utils'
import { Sidebar } from './sidebar'
import { TopBar } from './topbar'

/**
 * The console frame.
 *
 * Full viewport. The sidebar owns the full height, the top bar owns the width of the content
 * column, and the only element that scrolls is the content region — the document never grows a
 * scrollbar of its own.
 *
 * There is deliberately no max width on the content. This is a console; it uses the monitor it is
 * given. Screens that need a reading measure set one on their own prose.
 */
export function Shell({
  route,
  crumb,
  counts,
  onNavigate,
  onOpenPalette,
  children,
}: {
  route: Route
  crumb?: string
  counts: Partial<Record<Route, number>>
  onNavigate: (route: Route) => void
  onOpenPalette: () => void
  children: ReactNode
}) {
  const [navOpen, setNavOpen] = useState(false)

  return (
    <div className="bg-bg flex h-full overflow-hidden">
      {navOpen && (
        <button
          aria-label="Close navigation"
          onClick={() => setNavOpen(false)}
          className="bg-overlay animate-fade fixed inset-0 z-40 lg:hidden"
        />
      )}

      <Sidebar
        route={route}
        counts={counts}
        onNavigate={onNavigate}
        open={navOpen}
        onClose={() => setNavOpen(false)}
      />

      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar
          route={route}
          crumb={crumb}
          onOpenNav={() => setNavOpen(true)}
          onOpenPalette={onOpenPalette}
        />
        <main className="min-h-0 flex-1 overflow-y-auto">{children}</main>
      </div>
    </div>
  )
}

/**
 * A non-list screen's padding — Overview, Settings, a record detail.
 *
 * The same 20px rail the list scaffold uses, so a screen that is not a table still lines up with
 * one that is.
 */
export function Screen({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('px-5 pt-[22px] pb-5', className)}>{children}</div>
}

export function ScreenHeader({
  title,
  description,
  actions,
  meta,
}: {
  title: ReactNode
  description?: ReactNode
  actions?: ReactNode
  meta?: ReactNode
}) {
  return (
    <div className="mb-4 flex flex-wrap items-start gap-4">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2.5">
          {/* Plain case, 20px — the design's page title. */}
          <h1 className="text-text m-0 text-[20px] font-semibold tracking-[-0.028em]">{title}</h1>
          {meta}
        </div>
        {description && (
          <p className="text-muted mt-[5px] max-w-[90ch] text-[13px] text-pretty">{description}</p>
        )}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
  )
}
